import _ from 'lodash';
import {
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  TYPE_SPECIAL_EVENT_ENUM,
  LIST_RUN_EVENT,
} from '@/shared/config';
import { TBeatHrActiveTab } from '@/shared/model';

/**
 * Records or clears a bulk action applied to a whole event-type group, together with the filter state it was applied under. Deep-clones the existing change list, removes the group's entry on a resetAllChanges action, and otherwise appends a new entry or overwrites the found one's action type and filter snapshot.
 *
 * @param groupEventType - The event type the bulk action targets.
 * @param actionType - The bulk action applied, or resetAllChanges to drop the group's entry.
 * @param groupEventChanges - The recorded group changes, cloned rather than mutated.
 * @param filterSnapshot - The filter state captured when the action was applied.
 * @returns The cloned group-change list with the group's entry added, updated or removed.
 */
export const updateGroupEvents = (
  groupEventType: any,
  actionType: any,
  groupEventChanges: any[],
  filterSnapshot: any,
) => {
  const clonedGroupEventChanges = _.cloneDeep(groupEventChanges);
  if (actionType === 'resetAllChanges') {
    _.remove(clonedGroupEventChanges, (x) => x.groupEventType === groupEventType);
  } else {
    const foundGroupEventChange = _.find(
      clonedGroupEventChanges,
      (x) => x.groupEventType === groupEventType,
    );
    if (!foundGroupEventChange) {
      clonedGroupEventChanges.push({
        groupEventType,
        actionType,
        filterSnapshot,
      });
    } else {
      foundGroupEventChange.actionType = actionType;
      foundGroupEventChange.filterSnapshot = filterSnapshot;
    }
  }
  return clonedGroupEventChanges;
};

/**
 * Reports whether the events list for the active event tab is currently narrowed by a filter. Always false on the max and min HR tabs, otherwise true when hide-reviewed, capture-only or a sort order is set, or when the below-100 average HR filter applies and the tab is a run event.
 *
 * @param eventFilter - The current filter state, read for isHideReviewed, isCapture, sortOrder and isAvgHrBelow100.
 * @param activeButton - The event type of the active tab.
 * @returns A truthy value when any filter is in effect for the active tab.
 */
export const isAppliedFilterEvent = (
  eventFilter: any,
  activeButton: TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM | TBeatHrActiveTab,
) => {
  if (
    activeButton === TYPE_RHYTHM_EVENT_ENUM.MAX_HR ||
    activeButton === TYPE_RHYTHM_EVENT_ENUM.MIN_HR
  ) {
    return false;
  }
  const isAvgHrBelow100Applied =
    eventFilter.isAvgHrBelow100 &&
    LIST_RUN_EVENT.includes(activeButton as TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM);
  return (
    eventFilter.isHideReviewed ||
    eventFilter.isCapture ||
    eventFilter.sortOrder ||
    isAvgHrBelow100Applied
  );
};

/**
 * Reports whether a tachy, brady or pause tab is narrowed to one of its HR or duration sub-ranges. Returns false for every other tab and for the all-HR-ranges selection, and otherwise coerces the tab's stored special type to a boolean.
 *
 * @param specialEventTypes - The special sub-range selected per event type.
 * @param activeButton - The event type of the active tab.
 * @returns True when the active tab has a special sub-range selected.
 */
export const isAppliedFilterSpecialEvent = (
  specialEventTypes: any,
  activeButton: TYPE_RHYTHM_EVENT_ENUM | TYPE_BEAT_EVENT_ENUM,
) => {
  if (
    [
      TYPE_RHYTHM_EVENT_ENUM.TACHY,
      TYPE_RHYTHM_EVENT_ENUM.BRADY,
      TYPE_RHYTHM_EVENT_ENUM.PAUSE,
    ].includes(activeButton as TYPE_RHYTHM_EVENT_ENUM)
  ) {
    if (specialEventTypes[activeButton] === TYPE_SPECIAL_EVENT_ENUM.ALL_HR_RANGES) {
      return false;
    }
    return !!specialEventTypes[activeButton];
  }
  return false;
};
